import React, { FC, useState } from "react";
import Modal from "../../../../shared/modal/modal";
import styles from "./approve-reject-modal.module.scss";
import Button from "../../../ui-kit/button/button";
import { useAppSelector } from "../../../../hooks/useAppSelector";
import { changeStageStatus } from "../../../../services/api";
import succesIcon from "../../../../images/success.svg";
import rejectIcon from "../../../../images/error-120.svg";
import { getStageKey } from "../../../../services/reducers/utilities-journal";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface ApproveRejectModalProps {
  onClose: () => void;
  action: "approve" | "reject";
  depositPayed?: boolean;
}

const ApproveRejectModal: FC<ApproveRejectModalProps> = ({
  onClose,
  depositPayed,
  action,
}) => {
  const {
    selectedRow,
    stageTerritoryGeometry,
    buildingTerritoryGeometry,
    convertedGeometry,
    deletedFileIds,
  } = useAppSelector((state) => state.masterUtilities);

  const [comment, setComment] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [mode, setMode] = useState<"form" | "approved" | "rejected">("form");

  const statusId = action === "approve" ? 3 : 2;

  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleClose = () => {
    if (mode === "approved" || mode === "rejected") {
      onClose();
      navigate("/repository/utilities");
    } else {
      onClose();
    }
  };

  const handleSubmit = async () => {
    if (!selectedRow) return;

    setIsLoading(true);

    try {
      const stage = getStageKey(selectedRow.stage);

      const stageGeometry = convertedGeometry?.[stage];
      const files = stageGeometry
        ? Object.values(stageGeometry).map((file) => ({
            ...file,
            graphics: file.graphics.map((g) => ({
              ...g,
              attributes: Object.fromEntries(
                Object.entries(g.attributes).filter(([key]) => key !== "uuid"),
              ),
            })),
          }))
        : [];

      const payload = {
        stageId: selectedRow.stageId,
        statusId,
        ...(depositPayed !== undefined && { depositPayed }),
        files,
        ...(deletedFileIds.length > 0 && { deleteFiles: deletedFileIds }),
        ...(comment.trim() && { comment }),
        stageTerritoryGeometry: JSON.stringify(
          stageTerritoryGeometry[stage]?.geometry,
        ),
        ...(buildingTerritoryGeometry[stage] && {
          buildingTerritoryGeometry: JSON.stringify(
            buildingTerritoryGeometry[stage]?.geometry,
          ),
        }),
      };
      await changeStageStatus(payload);
      setMode(action === "approve" ? "approved" : "rejected");
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const title =
    action === "approve"
      ? t("approveRejectModal.approve")
      : t("approveRejectModal.reject");
  const primaryLabel =
    action === "approve"
      ? t("approveRejectModal.approveProject")
      : t("approveRejectModal.rejectProject");

  return (
    <Modal closeOnOverlayClick={mode !== "form"} onClose={handleClose}>
      <div className={styles.approveRejectModal}>
        <h1 className={styles.title}>{title}</h1>
        {mode === "form" ? (
          <>
            <div className={styles.qcResultComment}>
              <span className={styles.qcResultCommentLabel}>
                {t("approveRejectModal.comment")}
              </span>
              <textarea
                className={`${styles.qcResultTextarea} ${comment ? styles.filled : ""}`}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={t("approveRejectModal.commentPlaceholder")}
              />
            </div>
            <div className={styles.groupBtn}>
              <Button
                type="primary"
                className={styles.btn}
                disabled={isLoading}
                onClick={handleSubmit}
              >
                {t(
                  `approveRejectModal.${action === "approve" ? "approveBtn" : "rejectBtn"}`,
                )}
              </Button>
              <Button
                type="outline"
                className={styles.btn}
                onClick={handleClose}
              >
                {t("approveRejectModal.cancel")}
              </Button>
            </div>
          </>
        ) : (
          <>
            <img
              className={styles.image}
              src={mode === "approved" ? succesIcon : rejectIcon}
              alt={
                mode === "approved"
                  ? t("approveRejectModal.success")
                  : t("approveRejectModal.error")
              }
            />
            <span className={styles.message}>{primaryLabel}</span>
            <div className={styles.groupBtn}>
              <Button
                type="primary"
                className={styles.btn}
                disabled={isLoading}
                onClick={handleClose}
              >
                {t("approveRejectModal.done")}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ApproveRejectModal;
